import React from "react";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import Logo from "shared/Logo/Logo";
import { Disclosure } from "@headlessui/react";
import { NavLink } from "react-router-dom";
import { NavItemType } from "./NavigationItem";
import { NAVIGATION_DEMO } from "data/navigation";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import SocialsList from "shared/SocialsList/SocialsList";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useSelector } from "react-redux";
import { Box, Typography, Divider } from "@mui/material";

// Define playful colors to match the rest of the site
const colors = {
  primary: "#6A5ACD", // Soft purple
  secondary: "#FF9671", // Coral
  highlight: "#FFC75F", // Soft yellow
  accent: "#FF6F91", // Pink
  lightBg: "#FFF9F0", // Off-white
  border: "#FFE3B8", // Light orange
};

// Gradient options for decorative elements
const buttonGradients = [
  "linear-gradient(45deg, #FF6F91, #FF9671)",
  "linear-gradient(45deg, #845EC2, #D65DB1)",
  "linear-gradient(45deg, #FFC75F, #FF9671)",
  "linear-gradient(45deg, #00C9A7, #4CA1AF)",
];

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_DEMO,
  onClickClose,
}) => {
  const subscription = useSelector((state: any) => state.user.subscription);
  const subscriptionStatus = useSelector(
    (state: any) => state.user.subscriptionStatus
  );
  const isAuth = useSelector((state: any) => state.user.isAuthenticated);

  // Randomly pick a gradient for decorative elements
  const randomGradient = React.useMemo(() => {
    return buttonGradients[Math.floor(Math.random() * buttonGradients.length)];
  }, []);

  const checkSubs = (item: string) => {
    if (subscriptionStatus === "active" && item === "/member") {
      return false;
    }
    return true;
  };

  const _renderMenuChild = (item: NavItemType) => {
    return (
      <ul className="pl-5 py-1 text-base overflow-hidden">
        {item.children?.map((i, index) => (
          <Disclosure key={i.href + index} as="li" className="mb-1">
            <NavLink
              end
              to={{
                pathname: i.href || undefined,
              }}
              className={({ isActive }) =>
                `flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                  isActive
                    ? "text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300"
                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                }`
              }
            >
              <span className={`py-1 ${!i.children ? "block w-full" : ""}`}>
                {i.name}
              </span>
              {i.children && (
                <span
                  className="flex-1 flex"
                  onClick={(e) => e.preventDefault()}
                >
                  <Disclosure.Button
                    as="span"
                    className="flex justify-end flex-1"
                  >
                    <ChevronDownIcon
                      className="ml-2 h-4 w-4 text-gray-500 transition-transform"
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                </span>
              )}
            </NavLink>
            {i.children && (
              <Disclosure.Panel className="mt-1 ml-2 border-l-2 border-gray-100 dark:border-gray-800">
                {_renderMenuChild(i)}
              </Disclosure.Panel>
            )}
          </Disclosure>
        ))}
      </ul>
    );
  };

  const _renderItem = (item: NavItemType, index: number) => {
    return (
      <Disclosure key={item.id} as="li" className="mb-2">
        {checkSubs(item.href) ? (
          <NavLink
            end
            className={({ isActive }) =>
              `flex w-full items-center px-4 py-3 font-medium text-base rounded-lg transition-all duration-200 ${
                isActive
                  ? "text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300"
                  : "text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`
            }
            to={{
              pathname: item.href || undefined,
            }}
          >
            <span className={`${!item.children ? "block w-full" : ""}`}>
              {item.name}
            </span>
            {item.children && (
              <span className="flex-1 flex" onClick={(e) => e.preventDefault()}>
                <Disclosure.Button
                  as="span"
                  className="flex items-center justify-end flex-1"
                >
                  <ChevronDownIcon
                    className="ml-2 h-5 w-5 text-gray-500 transition-transform"
                    aria-hidden="true"
                  />
                </Disclosure.Button>
              </span>
            )}
          </NavLink>
        ) : null}
        {item.children && (
          <Disclosure.Panel className="mt-1.5 ml-2 border-l-2 border-gray-100 dark:border-gray-800">
            {_renderMenuChild(item)}
          </Disclosure.Panel>
        )}
      </Disclosure>
    );
  };

  return (
    <div className="w-full h-screen overflow-y-auto bg-white dark:bg-gray-900 shadow-lg">
      {/* Header with logo and close button */}
      <div className="relative px-2 py-1 border-b border-gray-100 dark:border-gray-800">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            transform: "scale(0.7)",
            mt: -1.5,
            mb: -1.5,
          }}
        >
          <Logo />
        </Box>
        <span className="absolute right-1 top-1 p-1 z-10">
          <ButtonClose onClick={onClickClose} />
        </span>
      </div>

      {/* Welcome message */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          background: `linear-gradient(to right, ${colors.lightBg}, white)`,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "0.9rem",
            fontStyle: "italic",
            lineHeight: 1.4,
            textAlign: "center",
          }}
        >
          Ανακαλύψτε τα καλύτερα μέρη για πάρτι και εξόδους!
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 0.5,
          }}
        >
          <SocialsList itemClass="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 text-xl dark:bg-gray-800 dark:text-gray-300 mx-1" />
        </Box>
      </Box>

      {/* Navigation items */}
      <ul className="px-3 py-4 space-y-1">{data.map(_renderItem)}</ul>

      {/* Button section */}
      {!isAuth && (
        <div
          className="px-4 py-4 mt-2"
          style={{
            background: colors.lightBg,
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          <div style={{ position: "relative" }}>
            {/* Decorative gradient element */}
            <div
              className="absolute top-0 right-0 w-20 h-20 opacity-10 -z-10"
              style={{
                background: randomGradient,
                borderRadius: "50%",
                transform: "translate(30%, -50%)",
                filter: "blur(25px)",
              }}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 2,
                "& > a": {
                  width: "100%",
                },
              }}
            >
              <div
                style={{
                  background: randomGradient,
                  borderRadius: "9999px",
                  padding: "2px",
                  boxShadow: "0 4px 14px 0 rgba(0,0,0,0.1)",
                }}
              >
                <ButtonPrimary href="/login" className="w-full !rounded-full">
                  Εγγραφή/Σύνδεση
                </ButtonPrimary>
              </div>
              <ButtonSecondary
                href="/subscription"
                className="w-full !rounded-full"
              >
                Γίνε Συνεργάτης
              </ButtonSecondary>
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavMobile;
